import { useEffect, useState } from 'react';
import api from '../../services/api';
import type { AcctDrillRow, AcctMemberType, AcctRowType } from '../../services/accountingDashboards.service';
import type { FilterCondition } from '../../components/FilterConditionRow';
import type { DrillDescriptor } from './accountingDrill';

interface DrillDataState {
  rows: AcctDrillRow[];
  /** What the members ARE — drives invoice selection; null for placeholders and before the first response. */
  memberType: AcctMemberType | null;
  /** How to DRAW them, which for two pro families is one row per masterpro. */
  rowType: AcctRowType | null;
  /** Whether this bucket accepts an orders filter (false for template buckets). */
  filterable: boolean;
  /** First load only. A refresh keeps the rows on screen — see `refreshing`. */
  loading: boolean;
  /** A background refetch over rows that are already displayed. */
  refreshing: boolean;
  error: string | null;
}

/**
 * Resolve a drill descriptor to rows. A placeholder never touches the network and reports
 * `notWired` so the card can say so; an endpoint descriptor GETs on mount and again on
 * every refresh tick (the page's shared 30s timer).
 */
export function useDrillData(
  descriptor: DrillDescriptor,
  refreshTick: number,
  /** The bucket's custom filter. Serialized into the request, so a change re-runs the
   *  effect through descriptorKey without the caller needing its own trigger. */
  conditions?: FilterCondition[],
) {
  // Lazy initializer so an endpoint descriptor's very first render already reports loading.
  const [state, setState] = useState<DrillDataState>(() => ({
    rows: [],
    memberType: null,
    rowType: null,
    // Assume filterable until told otherwise: only template buckets are not, and showing
    // the control for a beat is better than it appearing late on every other bucket.
    filterable: true,
    loading: descriptor.kind === 'endpoint',
    refreshing: false,
    error: null,
  }));

  // Key the effect on the descriptor's content, not its identity. Callers may pass a fresh
  // literal each render; an identity dependency would re-run the effect (and re-GET) on
  // every render, and the setState inside would keep it going forever.
  // The filter rides in the key so applying or clearing one refetches, exactly as a
  // descriptor change does.
  const descriptorKey = JSON.stringify(descriptor);
  const conditionsKey = JSON.stringify(conditions && conditions.length ? conditions : null);

  useEffect(() => {
    const target: DrillDescriptor = JSON.parse(descriptorKey);
    if (target.kind === 'placeholder') {
      setState({ rows: [], memberType: null, rowType: null, filterable: true, loading: false, refreshing: false, error: null });
      return;
    }
    const activeConditions: FilterCondition[] | null = JSON.parse(conditionsKey);
    let cancelled = false;
    // LOADING ONLY WHEN THERE IS NOTHING TO SHOW. TanTable paints a dimming overlay over
    // the whole grid while `loading`, which on the page's 30-second timer meant the table
    // greyed out on a cadence whether or not anything had changed. A refetch over rows
    // already on screen is `refreshing`: the rows stay, and swap when the answer lands.
    setState((s) => (s.rows.length
      ? { ...s, refreshing: true, error: null }
      : { ...s, loading: true, refreshing: false, error: null }));
    // The drill is a GET, so the filter travels as a JSON query param rather than a body —
    // see parse_conditions in accounting_dashboards.py for why the method stays GET.
    const params = {
      ...(target.params || {}),
      ...(activeConditions ? { conditions: JSON.stringify(activeConditions) } : {}),
    };
    // No params → a bare GET, so nothing has to reason about an empty query object.
    const request = Object.keys(params).length ? api.get(target.path, { params }) : api.get(target.path);
    request
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const rows: AcctDrillRow[] = data?.rows ?? [];
        setState({
          rows,
          memberType: data?.memberType ?? null,
          // Fall back to memberType so an older backend still draws something sane.
          rowType: data?.rowType ?? data?.memberType ?? null,
          filterable: data?.filterable !== false,
          loading: false,
          refreshing: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : 'Failed to load';
        // A failed REFRESH keeps the rows it already had. Blanking a populated table
        // because one poll failed loses more than it protects — the error says the
        // figures are no longer being updated, which is the thing worth knowing.
        setState((s) => (s.rows.length
          ? { ...s, refreshing: false, error: message }
          : { rows: [], memberType: null, rowType: null, filterable: true, loading: false, refreshing: false, error: message }));
      });
    return () => { cancelled = true; };
  }, [descriptorKey, refreshTick, conditionsKey]);

  return { ...state, notWired: descriptor.kind === 'placeholder' };
}
